import React, { useMemo, useState } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { Alert, Form } from "react-bootstrap";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import AppButton from "../layout/AppButton";
import { getToken } from "../auth/authConfig";
import { apiService } from "../../services/apiClient";
import { apiUrl } from "../../services/apiUrl";
import ToastNotification from "../layout/ToastNotification";

const AddVisualsDialog = ({ personaName, personaId }) => {
  const [open, setOpen] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportOptions, setReportOptions] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [selectedReportLabel, setSelectedReportLabel] = useState("");
  const [loadingSelectedReport, setLoadingSelectedReport] = useState(false);
  const [reportConfig, setReportConfig] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedPageName, setSelectedPageName] = useState("");
  const [availableVisuals, setAvailableVisuals] = useState([]);
  const [selectedVisualNames, setSelectedVisualNames] = useState([]);
  const [cart, setCart] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedVisuals, setSavedVisuals] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = (msg, severity = "success") => setToast({ open: true, message: msg, severity });

  const defaultCount = useMemo(
    () => cart.filter((item) => item.is_default === "YES").length,
    [cart]
  );

  const selectedPageDisplayName = useMemo(() => {
    const found = pages.find((item) => item.name === selectedPageName);
    return found ? found.displayName : "";
  }, [pages, selectedPageName]);

  const resetState = () => {
    setSelectedReportId("");
    setSelectedReportLabel("");
    setLoadingSelectedReport(false);
    setReportConfig(null);
    setPages([]);
    setSelectedPageName("");
    setAvailableVisuals([]);
    setSelectedVisualNames([]);
    setCart([]);
    setSaving(false);
    setSavedVisuals([]);
    setMessage({ type: "", text: "" });
  };

  const openDialog = async () => {
    setOpen(true);
    setMessage({ type: "", text: "" });

    if (reportOptions.length > 0) {
      return;
    }

    setLoadingReports(true);
    try {
      // Use group-scoped endpoints when personaId is available
      const [reportsRes, visualsRes] = await Promise.all([
        personaId
          ? apiService.get(apiUrl.aipdReportsByGroup(personaId))
          : apiService.get(apiUrl.aipdGetMenu()),
        apiService.get(apiUrl.aipdPersonaVisuals(personaId || undefined)),
      ]);

      // aipdReportsByGroup returns a plain array; getMenu returns { data: { reports } }
      const rawReports = personaId
        ? (Array.isArray(reportsRes.data) ? reportsRes.data : (reportsRes.data?.data?.reports || []))
        : (Array.isArray(reportsRes.data?.data?.reports) ? reportsRes.data.data.reports : []);

      const mapped = rawReports
        .filter((row) => Array.isArray(row) && row.length >= 2)
        .map((row) => ({ id: String(row[0]), label: row[1] }));

      // Use byGroup visuals for this persona, or all if no group
      const byGroup = Array.isArray(visualsRes.data?.byGroup) ? visualsRes.data.byGroup : [];
      const groupVisuals = personaId
        ? (byGroup.find(g => String(g.groupId) === String(personaId))?.visuals || [])
        : (Array.isArray(visualsRes.data?.all) ? visualsRes.data.all : []);

      setSavedVisuals(groupVisuals.map(v => ({
        visual_name: v.visualName,
        title:       v.title,
        report_name: v.reportName,
      })));
      setReportOptions(mapped);
    } catch (error) {
      setMessage({ type: "danger", text: "Unable to load reports." });
    } finally {
      setLoadingReports(false);
    }
  };

  const closeDialog = () => {
    setOpen(false);
    resetState();
  };

  const loadVisualsForPage = async (pageName, embeddedReportRef) => {
    try {
      if (!pageName || !embeddedReportRef) {
        return;
      }

      const pageList = await embeddedReportRef.getPages();
      const page = pageList.find((item) => item.name === pageName);
      if (!page) {
        setAvailableVisuals([]);
        setSelectedVisualNames([]);
        return;
      }

      const visuals = await page.getVisuals();
      const mappedVisuals = visuals
        .filter((visual) => visual && visual.name)
        .map((visual) => ({
          id: visual.name,
          title: visual.title || visual.displayName || visual.name,
          type: visual.type || "visual",
        }));

      setAvailableVisuals(mappedVisuals);

      // Pre-check visuals already saved for this page
      const preChecked = mappedVisuals
        .filter((v) => savedVisuals.some((s) => s.visual_name === v.id && s.report_name === pageName))
        .map((v) => v.id);
      setSelectedVisualNames(preChecked);
    } catch (error) {
      setMessage({ type: "danger", text: "Unable to load visuals for this page." });
      setAvailableVisuals([]);
      setSelectedVisualNames([]);
    }
  };

  const handleEmbeddedReport = (embeddedReport) => {
    window.personaVisualReport = embeddedReport;

    embeddedReport.on("loaded", async () => {
      try {
        const allPages = await embeddedReport.getPages();
        const visiblePages = allPages.filter((page) => page.visibility !== 1);
        const mappedPages = visiblePages.map((page) => ({
          name: page.name,
          displayName: page.displayName || page.name,
          isActive: page.isActive,
        }));
        setPages(mappedPages);

        const activePage = mappedPages.find((page) => page.isActive) || mappedPages[0];
        if (activePage) {
          setSelectedPageName(activePage.name);
          await loadVisualsForPage(activePage.name, embeddedReport);
        }
      } catch (error) {
        setPages([]);
      }
    });

    embeddedReport.on("pageChanged", async (event) => {
      const page = event?.detail?.newPage;
      if (!page) {
        return;
      }

      setSelectedPageName(page.name);
      await loadVisualsForPage(page.name, embeddedReport);
    });
  };

  const handleReportChange = async (event) => {
    const nextReportId = event.target.value;
    if (!nextReportId) {
      setSelectedReportId("");
      setSelectedReportLabel("");
      setReportConfig(null);
      setPages([]);
      setSelectedPageName("");
      setAvailableVisuals([]);
      setSelectedVisualNames([]);
      return;
    }

    const selected = reportOptions.find((item) => item.id === nextReportId);
    setSelectedReportId(nextReportId);
    setSelectedReportLabel(selected?.label || "");
    setLoadingSelectedReport(true);
    setReportConfig(null);
    setPages([]);
    setSelectedPageName("");
    setAvailableVisuals([]);
    setSelectedVisualNames([]);
    setMessage({ type: "", text: "" });

    try {
      const token = await getToken();
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/aipd/reports/${nextReportId}`, {
        method: "GET",
        headers: {
          "oidc-token": token,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Unable to load report");
      }

      const data = await response.json();
      setReportConfig(data?.data || null);
    } catch (error) {
      setMessage({ type: "danger", text: "Unable to open selected report." });
    } finally {
      setLoadingSelectedReport(false);
    }
  };

  const handlePageChange = async (event) => {
    const nextPageName = event.target.value;
    setSelectedPageName(nextPageName);

    try {
      const reportRef = window.personaVisualReport;
      if (reportRef && nextPageName) {
        const pageList = await reportRef.getPages();
        const page = pageList.find((item) => item.name === nextPageName);
        if (page) {
          await page.setActive();
        }
      }
    } catch (error) {
      // Keep flow alive even if explicit setActive fails.
    }

    await loadVisualsForPage(nextPageName, window.personaVisualReport);
  };

  const toggleVisual = (visualId) => {
    setSelectedVisualNames((prev) =>
      prev.includes(visualId)
        ? prev.filter((item) => item !== visualId)
        : [...prev, visualId]
    );
  };

  const addSelectedToCart = () => {
    if (!selectedReportId || !selectedPageName) {
      setMessage({ type: "danger", text: "Select a report and page first." });
      return;
    }

    if (selectedVisualNames.length === 0) {
      setMessage({ type: "danger", text: "Select at least one visual." });
      return;
    }

    const selectedRows = availableVisuals
      .filter((visual) => selectedVisualNames.includes(visual.id))
      .map((visual) => ({
        report_id: parseInt(selectedReportId, 10),
        report_name: selectedPageName,
        title: visual.title,
        visual_name: visual.id,
        userid: localStorage.getItem("userId") || "",
        user_group_id: personaId || undefined,
        is_default: "NO",
        reportLabel: selectedReportLabel,
        pageLabel: selectedPageDisplayName || selectedPageName,
      }));

    const merged = [...cart];
    selectedRows.forEach((row) => {
      const exists = merged.some(
        (item) =>
          item.report_id === row.report_id &&
          item.report_name === row.report_name &&
          item.visual_name === row.visual_name
      );
      if (!exists) {
        merged.push(row);
      }
    });

    setCart(merged);
    setSelectedVisualNames([]);
    setMessage({ type: "success", text: `${selectedRows.length} visual(s) added to cart.` });
  };

  const setCartItemDefault = (row, nextDefault) => {
    if (nextDefault === "YES") {
      const currentIsDefault = row.is_default === "YES";
      if (!currentIsDefault && defaultCount >= 2) {
        setMessage({
          type: "danger",
          text: "Maximum 2 visuals can be marked as default.",
        });
        return;
      }
    }

    setCart((prev) =>
      prev.map((item) => {
        const isTarget =
          item.report_id === row.report_id &&
          item.report_name === row.report_name &&
          item.visual_name === row.visual_name;

        if (!isTarget) {
          return item;
        }

        return {
          ...item,
          is_default: nextDefault,
        };
      })
    );

    setMessage({ type: "", text: "" });
  };

  const removeFromCart = (row) => {
    setCart((prev) =>
      prev.filter(
        (item) =>
          !(
            item.report_id === row.report_id &&
            item.report_name === row.report_name &&
            item.visual_name === row.visual_name
          )
      )
    );
  };

  const saveCart = async () => {
    if (cart.length === 0) {
      setMessage({ type: "danger", text: "Cart is empty." });
      return;
    }

    if (defaultCount > 2) {
      setMessage({
        type: "danger",
        text: "Maximum 2 visuals can be marked as default.",
      });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await apiService.post(apiUrl.aipdAddVisualsToCart(), {
        items: cart.map((item) => ({
          report_id:     item.report_id,
          report_name:   item.report_name,
          title:         item.title,
          visual_name:   item.visual_name,
          userid:        item.userid,
          user_group_id: item.user_group_id,
          is_default:    item.is_default,
        })),
      });

      setCart([]);
      showToast(`Saved. Inserted: ${response.data?.insertedCount || 0}, Skipped: ${response.data?.skippedCount || 0}`);
      // Close dialog and reset so next open re-fetches fresh data
      setOpen(false);
      resetState();
      setReportOptions([]);
    } catch (error) {
      showToast(error?.response?.data?.message || "Unable to save visuals.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        style={{ padding: "4px 10px", borderRadius: "6px", border: "1px solid #162869", background: "#eceffd", cursor: "pointer", color: "#162869", fontSize: "11px", fontWeight: 600 }}
        onClick={openDialog}
      >
        Add Visuals
      </button>

      <Dialog open={open} onClose={closeDialog} maxWidth="lg" fullWidth>
        <DialogTitle style={{ background: "var(--app-button-color)", color: "#fff", fontWeight: 500 }}>
          Add Visuals {personaName ? `- ${personaName}` : ""}
        </DialogTitle>
        <DialogContent style={{ marginTop: "10px" }}>
          {message.text && <Alert variant={message.type || "info"}>{message.text}</Alert>}

          {savedVisuals.length > 0 && (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", padding: "10px 14px", marginBottom: "14px", fontSize: "12px" }}>
              <div style={{ fontWeight: 600, color: "#14653a", marginBottom: "6px" }}>
                Already saved visuals ({savedVisuals.length})
              </div>
              {Object.entries(
                savedVisuals.reduce((acc, v) => {
                  const key = v.report_name || "Unknown page";
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(v.title || v.visual_name);
                  return acc;
                }, {})
              ).map(([page, titles]) => (
                <div key={page} style={{ marginBottom: "4px" }}>
                  <span style={{ fontWeight: 500, color: "#166534" }}>{page}:</span>{" "}
                  <span style={{ color: "#15803d" }}>{titles.join(", ")}</span>
                </div>
              ))}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Select Report</Form.Label>
            <Form.Select
              value={selectedReportId}
              disabled={loadingReports || loadingSelectedReport}
              onChange={handleReportChange}
            >
              <option value="">{loadingReports ? "Loading reports..." : "Select report"}</option>
              {reportOptions.map((report) => (
                <option key={report.id} value={report.id}>
                  {report.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {loadingSelectedReport && (
            <div className="mb-3 text-muted">Loading selected report...</div>
          )}

          {reportConfig?.embedReport && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Select Page</Form.Label>
                <Form.Select
                  value={selectedPageName}
                  onChange={handlePageChange}
                  disabled={pages.length === 0}
                >
                  <option value="">Select page</option>
                  {pages.map((page) => (
                    <option key={page.name} value={page.name}>
                      {page.displayName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <div
                style={{
                  height: "360px",
                  border: "1px solid #e5e5e5",
                  borderRadius: "6px",
                  overflow: "hidden",
                  marginBottom: "12px",
                }}
              >
                <PowerBIEmbed
                  embedConfig={{
                    type: "report",
                    id: `${reportConfig.embedReport.reportGuid}`,
                    embedUrl: reportConfig.embedReport.url,
                    accessToken: reportConfig.embedReport.embedToken,
                    tokenType: models.TokenType.Embed,
                    viewMode: models.ViewMode.View,
                    pageName: selectedPageName || undefined,
                    settings: {
                      navContentPaneEnabled: false,
                      filterPaneEnabled: false,
                      panes: {
                        filters: {
                          expanded: false,
                          visible: false,
                        },
                      },
                    },
                  }}
                  eventHandlers={new Map([["error", function () {}]])}
                  cssClassName={"report-style-class"}
                  getEmbeddedComponent={handleEmbeddedReport}
                />
              </div>

              <div
                style={{
                  maxHeight: "220px",
                  overflowY: "auto",
                  border: "1px solid #ececec",
                  borderRadius: "6px",
                  padding: "10px",
                  marginBottom: "10px",
                }}
              >
                {availableVisuals.length === 0 && (
                  <span style={{ color: "#666" }}>No visuals found for this page.</span>
                )}

                {availableVisuals.map((visual) => (
                  <div
                    key={visual.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #f3f3f3",
                      padding: "6px 0",
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`persona_visual_${visual.id}`}
                      checked={selectedVisualNames.includes(visual.id)}
                      onChange={() => toggleVisual(visual.id)}
                      label={`${visual.title} (${visual.type})`}
                    />
                  </div>
                ))}
              </div>

              <AppButton variant="fill" onClick={addSelectedToCart} style={{ marginBottom: "14px" }}>
                Add Selected To Cart
              </AppButton>
            </>
          )}

          <div
            style={{
              border: "1px solid #ececec",
              borderRadius: "6px",
              padding: "10px",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: "8px" }}>
              Cart ({cart.length}) - Default selected: {defaultCount}/2
            </div>

            {cart.length === 0 && <span style={{ color: "#666" }}>No visuals in cart.</span>}

            {cart.map((item, index) => (
              <div
                key={`${item.report_id}_${item.report_name}_${item.visual_name}_${index}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #f3f3f3",
                  padding: "6px 0",
                }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{item.title || item.visual_name}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    {item.reportLabel || "Report"} / {item.pageLabel}
                  </div>
                  <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "12px", color: "#555" }}>Is Default:</span>
                    <Form.Check
                      inline
                      type="radio"
                      id={`default_yes_${item.report_id}_${item.visual_name}_${index}`}
                      name={`default_${item.report_id}_${item.report_name}_${item.visual_name}_${index}`}
                      label="Yes"
                      checked={item.is_default === "YES"}
                      onChange={() => setCartItemDefault(item, "YES")}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id={`default_no_${item.report_id}_${item.visual_name}_${index}`}
                      name={`default_${item.report_id}_${item.report_name}_${item.visual_name}_${index}`}
                      label="No"
                      checked={item.is_default !== "YES"}
                      onChange={() => setCartItemDefault(item, "NO")}
                    />
                  </div>
                </div>
                <AppButton variant="secondary" onClick={() => removeFromCart(item)}>
                  Remove
                </AppButton>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions style={{ marginRight: "30px" }}>
          <AppButton variant="secondary" onClick={closeDialog} disabled={saving}>
            Close
          </AppButton>
          <AppButton variant="fill" disabled={saving} onClick={saveCart}>
            {saving ? (
              <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span><span style={{ paddingLeft: "8px" }}>Saving...</span></>
            ) : "Add Visuals to Persona"}
          </AppButton>
        </DialogActions>
      </Dialog>
      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast(t => ({ ...t, open: false }))}
      />
    </>
  );
};

export default AddVisualsDialog;
